import { FormUnit, FoodsTags, FoodsIcon } from "@foodpilot/foods";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CalculatorActionIcon } from "src/components/Actions/Common/Calculator/CalculatorActionIcon";
import { NonCalculatorActionIcon } from "src/components/Actions/Common/Calculator/NonCalculatorActionIcon";
import { CatalogSubgroup } from "src/context/Actions/useActionScore";
import { useCatalogContext } from "../context/useCatalogContext";
import { Link } from "react-router";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";

type Props = {
    parentId: number;
    group: CatalogSubgroup;
};
export const SubgroupBlock = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { urls } = useActionsContext();
    const { hideAddedActions } = useCatalogContext();
    const { formatScore, getColor, selectedScorePrecision } = useEntityScores();

    const { group, parentId } = props;

    const shouldHide = hideAddedActions && group.canHide;
    if (shouldHide) {
        return null;
    }

    return (
        <Stack>
            <Stack
                border={`1px solid ${theme.custom.grey[600]}`}
                borderRadius={"12px 12px 0px 0px"}
                padding={theme.spacing(2)}
                bgcolor={theme.custom.grey[200]}
                justifyContent={"space-between"}
                flexDirection={"row"}
            >
                <Stack flexDirection={"row"} gap={theme.spacing(1)}>
                    {group.iconName && <FoodsIcon size={4} icon={group.iconName} />}
                    <Typography variant="h3">{group.title}</Typography>
                </Stack>
                {group.subgroupImpact !== null && (
                    <ApproximateImpactTooltip
                        value={group.subgroupImpact}
                        precision={selectedScorePrecision}
                        unit={group.unit ?? ""}
                    >
                        <FormUnit
                            value={formatScore(group.subgroupImpact) ?? ""}
                            unit={group.unit ?? ""}
                            color={getColor(group.subgroupImpact)}
                        />
                    </ApproximateImpactTooltip>
                )}
            </Stack>
            <Stack
                border={`1px solid ${theme.custom.grey[600]}`}
                borderTop={0}
                borderRadius={`0px 0px 12px 12px`}
                bgcolor={"white"}
            >
                <Stack
                    padding={theme.spacing(2)}
                    gap={theme.spacing(2)}
                    divider={<Box borderTop={`1px solid ${theme.custom.grey[600]}`} />}
                >
                    {group.items.flatMap((actionItem) => {
                        const url = urls.catalogGroupItem(parentId, actionItem.id);
                        if (actionItem.isEngaged && hideAddedActions) return [];
                        return [
                            <Link
                                key={actionItem.id}
                                to={url}
                                style={{
                                    textDecoration: "none",
                                    color: "currentcolor",
                                }}
                            >
                                <Stack
                                    sx={{
                                        cursor: "pointer",
                                        borderRadius: 1,
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": {
                                            background: `radial-gradient(ellipse at center, ${theme.palette.primary.main}15, ${theme.palette.common.white})`,
                                            "& .MuiTypography-root": {
                                                fontWeight: 700,
                                                // overflow: "hidden",
                                            },
                                        },
                                    }}
                                >
                                    <Stack
                                        flexDirection={"row"}
                                        justifyContent={"space-between"}
                                        gap={theme.spacing(3)}
                                    >
                                        <Typography variant="body-medium">{actionItem.title}</Typography>
                                        <Stack flexDirection={"row"} gap={theme.spacing(3)} alignItems={"center"}>
                                            {actionItem.isEngaged && (
                                                <FoodsTags
                                                    startIcon="check"
                                                    variant="tags-green"
                                                    label={t("action_plan.action_added_to_plan", "Added")}
                                                    disabled={true}
                                                />
                                            )}
                                            {actionItem.isCalculable ?
                                                <CalculatorActionIcon />
                                            :   <NonCalculatorActionIcon />}
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Link>,
                        ];
                    })}
                </Stack>
            </Stack>
        </Stack>
    );
};
